import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Loader2, LogOut, RefreshCw, Search, Send, ShieldCheck, Download, AlertTriangle, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { COMPANY } from "@/lib/constants";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
// Use sessionStorage (cleared on tab close) instead of localStorage for credentials.
// This still requires the user to re-login on a new tab, which is the safer default.
const SS_KEY = "cabsab_admin_session";

const statusBadge = (status, channel) => {
  if (status === "sent" && channel === "whatsapp") {
    return <Badge data-testid="status-badge" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">WhatsApp ✓</Badge>;
  }
  if (status === "sent_sms_fallback") {
    return <Badge data-testid="status-badge" className="bg-blue-100 text-blue-700 hover:bg-blue-100">SMS ✓</Badge>;
  }
  if (status === "not_configured") {
    return <Badge data-testid="status-badge" className="bg-amber-100 text-amber-800 hover:bg-amber-100">Manual (no API)</Badge>;
  }
  if (status === "failed") {
    return <Badge data-testid="status-badge" className="bg-rose-100 text-rose-700 hover:bg-rose-100">Failed</Badge>;
  }
  return <Badge data-testid="status-badge" className="bg-slate-100 text-slate-700 hover:bg-slate-100">{status || "—"}</Badge>;
};

function LoginScreen({ onLoggedIn }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) throw new Error("Invalid credentials");
      const data = await res.json();
      // Persist only for the current tab; clears on tab close. Password kept in-memory only
      // is impractical for our 20s poll, but sessionStorage minimises XSS exfil to a single tab.
      try {
        sessionStorage.setItem(SS_KEY, JSON.stringify({ username, password }));
      } catch (storageErr) {
        // Storage may be disabled in private mode — login still proceeds in memory
      }
      onLoggedIn({ username, password, ...data });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4" data-testid="admin-login-screen">
      <Card className="w-full max-w-md border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
        <CardHeader className="space-y-2 pb-2">
          <div className="flex items-center gap-2 text-[#19A8E8] font-semibold text-sm uppercase tracking-wider">
            <ShieldCheck className="h-4 w-4" /> Admin Access
          </div>
          <CardTitle className="text-2xl font-extrabold text-[#0F172A]">Cabsab Lead Dashboard</CardTitle>
          <p className="text-sm text-slate-500">Sign in to view and manage customer enquiries.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Username</Label>
              <Input data-testid="admin-username-input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Password</Label>
              <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required className="h-11" />
            </div>
            {error && (
              <div data-testid="admin-login-error" className="text-sm text-rose-600 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> {error}
              </div>
            )}
            <Button type="submit" disabled={busy} data-testid="admin-login-submit" className="w-full h-11 rounded-full bg-[#19A8E8] hover:bg-[#0E8AC4] text-white font-semibold">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in…</> : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function StatBanner({ session }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase font-bold tracking-wider text-slate-500">Admin WhatsApp</div>
        <div className="mt-1 font-semibold text-[#0F172A]">{COMPANY.phoneDisplay}</div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase font-bold tracking-wider text-slate-500">WhatsApp API</div>
        <div className="mt-1 font-semibold">
          {session?.whatsapp_configured
            ? <span className="text-emerald-600">Connected (auto-delivery)</span>
            : <span className="text-amber-600">Not configured — using click-to-chat fallback</span>}
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase font-bold tracking-wider text-slate-500">SMS Fallback</div>
        <div className="mt-1 font-semibold">
          {session?.sms_configured
            ? <span className="text-emerald-600">Connected</span>
            : <span className="text-slate-500">Disabled</span>}
        </div>
      </div>
    </div>
  );
}

function LeadsTable({ leads, onResend, resendingId }) {
  if (!leads.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500" data-testid="admin-empty-state">
        No enquiries yet. They'll appear here the moment a customer submits the booking form.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="admin-leads-table">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-4 py-3 font-bold">Submitted</th>
            <th className="text-left px-4 py-3 font-bold">Customer</th>
            <th className="text-left px-4 py-3 font-bold">Mobile</th>
            <th className="text-left px-4 py-3 font-bold">Pickup → Drop</th>
            <th className="text-left px-4 py-3 font-bold">Journey</th>
            <th className="text-left px-4 py-3 font-bold">Vehicle</th>
            <th className="text-left px-4 py-3 font-bold">Pax</th>
            <th className="text-left px-4 py-3 font-bold">Notify</th>
            <th className="text-right px-4 py-3 font-bold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {leads.map((l) => {
            const submitted = l.created_at ? new Date(l.created_at).toLocaleString("en-IN", { hour12: true }) : "—";
            return (
              <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 whitespace-nowrap text-slate-600">{submitted}</td>
                <td className="px-4 py-3 font-semibold text-[#0F172A]">{l.full_name}</td>
                <td className="px-4 py-3 tabular-nums">
                  <a href={`tel:+91${l.mobile}`} className="text-[#19A8E8] hover:underline">+91 {l.mobile}</a>
                </td>
                <td className="px-4 py-3 max-w-[280px]">
                  <div className="truncate" title={l.pickup}>📍 {l.pickup}</div>
                  <div className="truncate text-slate-500" title={l.drop}>→ {l.drop || "—"}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div>{l.journey_date}</div>
                  {l.return_date && <div className="text-slate-500 text-xs">↩ {l.return_date}</div>}
                </td>
                <td className="px-4 py-3">{l.vehicle}</td>
                <td className="px-4 py-3 tabular-nums">{l.passengers}</td>
                <td className="px-4 py-3">{statusBadge(l.notification_status, l.notification_channel)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <a
                      href={`https://wa.me/91${l.mobile}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-wa-customer-${l.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700"
                      title="Message customer on WhatsApp"
                    >
                      <MessageCircle className="h-3.5 w-3.5" /> Chat
                    </a>
                    <a
                      href={`tel:+91${l.mobile}`}
                      data-testid={`admin-call-customer-${l.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#19A8E8] hover:text-[#0E8AC4]"
                      title="Call customer"
                    >
                      <Phone className="h-3.5 w-3.5" /> Call
                    </a>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`admin-resend-${l.id}`}
                      onClick={() => onResend(l.id)}
                      disabled={resendingId === l.id}
                      className="h-8 px-2.5 rounded-lg text-xs"
                    >
                      {resendingId === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      <span className="ml-1">Resend</span>
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminDashboard() {
  const [session, setSession] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [resendingId, setResendingId] = useState(null);

  const authHeaders = useMemo(() => ({
    "x-admin-user": session?.username || "",
    "x-admin-pass": session?.password || "",
  }), [session]);

  const fetchLeads = useCallback(async (opts = {}) => {
    if (!session) return;
    setLoading(true);
    try {
      const url = new URL(`${API_BASE}/api/admin/leads`);
      url.searchParams.set("limit", "200");
      if (opts.q !== undefined ? opts.q : query) url.searchParams.set("q", opts.q !== undefined ? opts.q : query);
      const res = await fetch(url.toString(), { headers: authHeaders });
      if (res.status === 401) {
        toast.error("Session expired. Please sign in again.");
        try { sessionStorage.removeItem(SS_KEY); } catch (_) { /* noop */ }
        setSession(null);
        return;
      }
      if (!res.ok) throw new Error("Failed to load leads");
      const data = await res.json();
      setLeads(data.items || []);
    } catch (err) {
      toast.error(err.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [session, authHeaders, query]);

  // Restore session on mount (sessionStorage = scoped to this tab only)
  useEffect(() => {
    let cached = null;
    try { cached = sessionStorage.getItem(SS_KEY); } catch (_) { /* private mode */ }
    if (!cached) return;
    try {
      const creds = JSON.parse(cached);
      // verify by hitting login endpoint (re-validates and gets integration status)
      fetch(`${API_BASE}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(creds),
      })
        .then(async (r) => {
          if (r.ok) {
            const data = await r.json();
            setSession({ ...creds, ...data });
          } else {
            try { sessionStorage.removeItem(SS_KEY); } catch (_) { /* noop */ }
          }
        })
        .catch(() => {
          /* network error — keep cached creds; user can retry */
        });
    } catch (_) {
      try { sessionStorage.removeItem(SS_KEY); } catch (__) { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial fetch + poll every 20s for live updates
  useEffect(() => {
    if (!session) return;
    fetchLeads();
    const id = setInterval(() => fetchLeads(), 20000);
    return () => clearInterval(id);
  }, [session, fetchLeads]);

  const handleResend = async (leadId) => {
    setResendingId(leadId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leads/${leadId}/resend`, {
        method: "POST",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error("Resend failed");
      const data = await res.json();
      const st = data?.notification?.status;
      if (st === "sent" || st === "sent_sms_fallback") {
        toast.success("Notification resent successfully");
      } else if (st === "not_configured") {
        toast.warning("WhatsApp API not configured. Set Twilio creds in backend .env to enable auto-delivery.");
      } else {
        toast.error("Resend failed — check backend logs");
      }
      await fetchLeads();
    } catch (err) {
      toast.error(err.message || "Resend failed");
    } finally {
      setResendingId(null);
    }
  };

  const exportCsv = () => {
    if (!leads.length) {
      toast.info("No leads to export");
      return;
    }
    const headers = ["Submitted", "Name", "Mobile", "Pickup", "Drop", "Journey Date", "Return Date", "Vehicle", "Passengers", "Source", "Notify Status", "Channel"];
    const rows = leads.map((l) => [
      l.created_at, l.full_name, `+91${l.mobile}`, l.pickup, l.drop || "",
      l.journey_date, l.return_date || "", l.vehicle, l.passengers, l.source || "",
      l.notification_status || "", l.notification_channel || "",
    ]);
    const csv = [headers, ...rows].map((r) =>
      r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cabsab-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${leads.length} leads`);
  };

  const logout = () => {
    try { sessionStorage.removeItem(SS_KEY); } catch (_) { /* noop */ }
    setSession(null);
    setLeads([]);
  };

  if (!session) {
    return <LoginScreen onLoggedIn={setSession} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard-root">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Cabsab" className="h-9 w-auto" />
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-[#19A8E8]">Admin</div>
              <div className="text-base font-extrabold text-[#0F172A] leading-tight">Lead Management</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-export-btn" variant="outline" size="sm" onClick={exportCsv} className="rounded-full">
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
            <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={logout} className="rounded-full text-slate-600 hover:text-rose-600">
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StatBanner session={session} />

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchLeads()}
              placeholder="Search by name, mobile, pickup, drop, vehicle…"
              className="h-11 pl-9 bg-white"
            />
          </div>
          <Button data-testid="admin-refresh-btn" onClick={() => fetchLeads()} disabled={loading} variant="outline" className="h-11 rounded-full">
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Refresh
          </Button>
        </div>

        <div className="text-xs text-slate-500 mb-3" data-testid="admin-leads-count">
          Showing <span className="font-semibold text-[#0F172A]">{leads.length}</span> enquiries · auto-refreshes every 20s
        </div>

        <LeadsTable leads={leads} onResend={handleResend} resendingId={resendingId} />
      </div>
    </div>
  );
}
